import { buscarFechaCaducidad, acumularProducto, esRuido, aNumero, contarLetras } from "./comun";

/**
 * Parsea las líneas de texto extraídas de un PDF de Juan Pablo Hernández Cervera /
 * Sabela Pharmacy (RFC: HECJ870814GCA). Retorna un Map<clave, { lotes, cantidadLinea }>.
 *
 * Maqueta de la fila de producto:
 *   CANTIDAD | CODIGO SAT | CLAVE UNID. / UNI. MEDIDA | DESCRIPCION | LOTE | CADUCIDAD |
 *   PRECIO | DESCUENTO | IVA | IMPORTE
 *
 * Ejemplo real (folio 33752), tal como suele partirlo pdfjs:
 *   1.00 51111711 E48
 *   PIEZA
 *   DOXORRUBICINA, ZODOX 50 MG SOL. INYE, ACCORD P2600572 31-07-27 $840 $0 002, IVA,
 *
 * Las celdas van centradas verticalmente, así que la CANTIDAD cae en una fila visual
 * distinta a la descripción. Se resuelve recordando la última cabecera
 * "cantidad + clave SAT" y consumiéndola al encontrar el producto; si pdfjs sí unió todo
 * en una sola fila, la misma cabecera se detecta al inicio del renglón y se recorta.
 *
 * Si la cantidad no aparece por ningún lado, el lote se emite con cantidad null: el usuario
 * recibe lote y caducidad prellenados y sólo teclea la cantidad. No se inventa el dato.
 */

// Fila de cabecera de la partida: "1.00 51111711 E48"
const CABECERA = /^(\d+(?:\.\d+)?)\s+(\d{8})(?:\s|$)/;
// Un lote de verdad trae al menos un dígito; sin esto, cualquier palabra pegada a una
// fecha del pagaré ("el día 12/Septiembre/2026") se colaba como lote.
const LOTE = /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9\-/.]{1,19}$/;
const UNIDAD_SAT = /^[A-Z0-9]{2,4}$/;
const UNIDAD_TEXTO = /^(PIEZA|PIEZAS|PZA|PZ|CAJA|FRASCO)$/i;

/** Quita la cabecera "cantidad claveSat [unidad] [PIEZA]" si quedó pegada a la descripción. */
function recortarCabecera(tokens) {
  if (tokens.length < 3) return { tokens, cantidad: null };
  if (!/^\d+(\.\d+)?$/.test(tokens[0]) || !/^\d{8}$/.test(tokens[1])) {
    return { tokens, cantidad: null };
  }

  let corte = 2;
  if (tokens[corte] && UNIDAD_SAT.test(tokens[corte])) corte++;
  if (tokens[corte] && UNIDAD_TEXTO.test(tokens[corte])) corte++;

  return { tokens: tokens.slice(corte), cantidad: aNumero(tokens[0]) };
}

export function parsearLineasSabela(allLines) {
  const productsByDesc = new Map();
  let cantidadPendiente = null;

  for (const rawLine of allLines || []) {
    const linea = String(rawLine ?? "").replace(/\s+/g, " ").trim();
    if (!linea || esRuido(linea)) continue;

    const fecha = buscarFechaCaducidad(linea);

    if (!fecha) {
      // Fila de cabecera: guarda la cantidad para el producto que viene
      const cabecera = linea.match(CABECERA);
      if (cabecera) cantidadPendiente = aNumero(cabecera[1]);
      continue;
    }

    // Sabela imprime los precios con signo de pesos; sin eso no es una fila de partida
    const despues = linea.slice(fecha.indice + fecha.texto.length);
    if (!despues.includes("$")) continue;

    const recorte = recortarCabecera(linea.slice(0, fecha.indice).split(" ").filter(Boolean));
    const tokens = recorte.tokens;
    if (tokens.length < 2) continue;

    const lote = tokens[tokens.length - 1];
    if (!LOTE.test(lote)) continue;

    const descripcion = tokens.slice(0, -1).join(" ").trim();
    if (contarLetras(descripcion) < 6) continue;

    acumularProducto(productsByDesc, {
      descripcion,
      lote,
      caducidad: fecha.fecha,
      cantidad: recorte.cantidad ?? cantidadPendiente,
    });
    cantidadPendiente = null;
  }

  return productsByDesc;
}

export default parsearLineasSabela;
